import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { DropAlignment } from 'src/app/models/layout/drop-alignment';
import { DropSettings } from 'src/app/models/drop-settings';
import { ShelfType } from 'src/app/models/layout/shelf-type';
import { PlanogramDrop } from "src/app/models/layout/planogram-drop";
import { PlanogramProduct } from "src/app/models/layout/planogram-product";
import { ContextMenuService } from 'src/app/services/contextmenu.service';
import { LayoutService } from 'src/app/services/layout.service';
import { PlanogramService } from 'src/app/services/planogram.service';
import { ZoomService } from 'src/app/services/zoom.service';
import { PlanogramShelf } from 'src/app/models/layout/planogram-shelf';
import { ShelfSettings } from 'src/app/models/shelf-settings';
import { DragContextService } from 'src/app/services/dragcontext.service';
import { PlaceholderService } from 'src/app/services/placeholder.service';
import { CapabilitiesService } from 'src/app/services/capabilities.service';
import { CapabilityType } from 'src/app/models/capability-type';
import { NgClass, NgIf, AsyncPipe } from '@angular/common';
import { PlanogramManualShelfComponent } from '../planogram-manual-shelf/planogram-manual-shelf.component';

export interface ShelfDragData {
  type: string;
  dropIndex: number;
  shelfIndex: number;
}

@Component({
    selector: 'app-planogram-manual-drop',
    templateUrl: './planogram-manual-drop.component.html',
    styleUrls: ['./planogram-manual-drop.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgIf, PlanogramManualShelfComponent, AsyncPipe]
})
export class PlanogramManualDropComponent implements OnInit, OnChanges, OnDestroy {
  @Input() drop: PlanogramDrop | null = null;
  @Input() dropIndex: number = -1;
  @Input() startingShelfNumber: number = -1;
  dropId: string = '';
  hasShelves: boolean = false;
  shelfStyles$ = new BehaviorSubject<{[key: number]: any}>({});
  wrapperClass$ = new BehaviorSubject<string>('justify-content-between');
  dropSettings : DropSettings | null = null;
  private subscriptions: Subscription[] = [];

  constructor(
    private zoomService: ZoomService,
    private layoutService: LayoutService,
    private planogramService: PlanogramService,
    private contextMenuService: ContextMenuService,
    private dragContextService: DragContextService,
    private capabilitiesService: CapabilitiesService,
    public placeholderService: PlaceholderService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.dropId = `drop-${this.dropIndex}`;
    this.hasShelves = this.drop != null ? this.drop.shelves.length > 0 : false;
    this.dropSettings = this.layoutService.getDropSettings(this.dropIndex);

    this.subscriptions.push(
      this.layoutService.layout$.subscribe(() => {
        const currentSettings = this.layoutService.getDropSettings(this.dropIndex);
        this.dropSettings = currentSettings;
        this.updateAlignment(currentSettings);
        this.updateShelfStyles();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.planogramService.shelfSettings$.subscribe(() => {
        this.updateShelfStyles();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.push(
      this.zoomService.zoom$.subscribe(() => {
        this.updateShelfStyles();
        this.cdr.markForCheck();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['dropIndex']) {
      this.hasShelves = this.drop != null ? this.drop.shelves.length > 0 : false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  getShelfSettings(shelfIndex: number): ShelfSettings | undefined {
    return this.dropSettings?.shelfSettings[shelfIndex];
  }

  getShelfNumber(shelfIndex: number): number {
    if (!this.drop) return 0;
    
    const startingNumber = this.startingShelfNumber;
    // Since we want to number from bottom to top, reverse the index
    return startingNumber + (this.drop.shelves.length - 1 - shelfIndex);
  }

  updateAlignment(settings: DropSettings | null) {
    if (!settings) {
      this.wrapperClass$.next('justify-content-between');
      return;
    }

    const alignment = Number(settings.alignment)

    switch (alignment) {
      case DropAlignment.TOP:
        this.wrapperClass$.next('justify-content-start');
        break;
      case DropAlignment.BOTTOM:
        this.wrapperClass$.next('justify-content-end');
        break;
      case DropAlignment.SPACE_BETWEEN:
        this.wrapperClass$.next('justify-content-between');
        break;
      default:
        this.wrapperClass$.next('justify-content-between');
    }
  }

  getShelfType(shelf: PlanogramShelf) {
    return ShelfType[shelf.shelfType];
  }

  getShelfDragData(shelfIndex: number): ShelfDragData {
    return {
      type: 'shelf',
      dropIndex: this.dropIndex,
      shelfIndex: shelfIndex
    }
  }

  getDropStyle() {
    if (this.drop == null) { return {} }
    
    // Use drop-specific footer height if available, otherwise use planogram default
    const effectiveFooterHeight = this.dropSettings?.footerHeight ?? this.planogramService.getDropFooterHeight();
    let footerHeight = effectiveFooterHeight * this.zoomService.getCurrentZoom();
    
    return {
      'padding-bottom': `${footerHeight}px`
    }
  }

  private updateShelfStyles() {
    if (!this.drop) return;

    const spacing = this.drop.minSpacing ?? this.planogramService.getShelfMinimumSpacing() * this.zoomService.getCurrentZoom();
    
    const styles = this.drop.shelves.reduce((acc, shelf, index) => {
      if (index > 0 && shelf.shelfType == ShelfType.REGULAR) {
        acc[index] = { marginTop: `${spacing}px` };
      } else {
        acc[index] = {};
      }
      return acc;
    }, {} as {[key: number]: any});

    this.shelfStyles$.next(styles);
  }

  getPreviewProductStyle(product: PlanogramProduct) {
    if (!product)
      return {}
    let productRef = this.layoutService.planogramItems.get(product.productId) ?? null;
    if (!productRef)
      return {}

    let width = this.zoomService.getCurrentZoom() * productRef.effectiveDimensions.width;
    let height = this.zoomService.getCurrentZoom() * productRef.effectiveDimensions.height;

    return {
      'pointer-events': 'none',
      width: `${width}px`,
      height: `${height}px`
    }
  }

  onDropRightClick(event: MouseEvent): void {
    event.preventDefault();
    this.contextMenuService.openDropContextMenu(event, this.dropIndex);
  }

  onShelfMouseDown(event: MouseEvent, shelfIndex: number) {
    if (event.button !== 0) return;

    {{ // Prevent shelf dragging if user can't edit
    if (!this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM)) return; }}

    event.preventDefault();
    event.stopPropagation();

    const wrapper = (event.target as HTMLElement).closest('.shelf-wrapper');
    if (!wrapper) return;

    this.dragContextService.onMouseDownFromShelfHandle(
      this.dropIndex,
      shelfIndex,
      wrapper as HTMLElement,
      event
    );
  }

  onShelfRightClick(event: MouseEvent, shelfIndex: number): void {
    event.stopImmediatePropagation();
    event.preventDefault();
    this.contextMenuService.openShelfContextMenu(event, this.dropIndex, shelfIndex);
  }

  onDragOver(event: MouseEvent): void {
    if (!this.dragContextService.isDraggingShelf()) return;
    {{ if (!this.capabilitiesService.hasCapability(CapabilityType.EDIT_PLANOGRAM)) return; }}

    const ctx = this.dragContextService.getContext();
    if (ctx.source?.type !== 'shelf' || !this.drop) return;

    const shelves = this.drop.shelves ?? [];
    const shelfElements = Array.from(
      (event.currentTarget as HTMLElement).querySelectorAll('.shelf-wrapper')
    ) as HTMLElement[];

    // Default to appending at the end if no shelves are present
    let targetIndex = shelfElements.length;

    if (shelfElements.length > 0) {
      const mouseY = event.clientY;
      for (let i = 0; i < shelfElements.length; i++) {
        const rect = shelfElements[i].getBoundingClientRect();
        const midY = rect.top + rect.height / 2;

        if (mouseY < midY) {
          targetIndex = i;
          break;
        }
      }
    }

    const draggedShelf = ctx.draggedShelf;
    if (draggedShelf) {
      // Prevent redundant updates
      const placeholder = this.placeholderService.getCurrentPlaceholder();
      if (
        placeholder.type !== 'shelf' ||
        placeholder.dropIndex !== this.dropIndex ||
        placeholder.shelfIndex !== targetIndex
      ) {
        this.placeholderService.setShelfPlaceholder(this.dropIndex, targetIndex, draggedShelf);
      }
    }
  }

  onDragLeave(event: MouseEvent) {
    const related = event.relatedTarget as HTMLElement;
    if (!related || !(event.currentTarget as HTMLElement).contains(related)) {
      this.placeholderService.clearPlaceholder();
    }
  }
}